import { useMemo } from 'react';
import {
  Card,
  Frame,
  Grid,
  IconCard,
  SageClassnames,
  useSageBreakpoint,
} from '@kajabi/sage-react';

import PlayButton from '../shared/PlayButton.png';

interface TransformedUnsplashObject {
  image_url: string;
  alt_description?: string;
}

interface PropsType {
  userData: any;
  previewImageUrls: TransformedUnsplashObject[];
  heroImageUrl: string;
}

const CoursePreview = ({
  userData,
  previewImageUrls,
  heroImageUrl,
}: PropsType) => {
  const courseData = useMemo(() => {
    const data = userData.ai_data.courses;

    if (previewImageUrls.length > 0) {
      data.sections.map((section: any, x: number) => {
        section.lessons.map((lesson: any, i: number) => {
          if (x === 0) {
            lesson.image = previewImageUrls[i]; // sets images as 0 1 2 from previewImageUrls array
          } else if (x === 1) {
            lesson.image = previewImageUrls[3 + i]; // sets images as 3 4 5 from previewImageUrls array
          } else {
            lesson.image = previewImageUrls[6 + i]; // sets images as 6 7 8 from previewImageUrls array
          }
        });
      });
    }
    return data;
  }, [userData, previewImageUrls]);

  const breakpoints = useSageBreakpoint();

  return (
    <Frame
      style={{
        width: breakpoints.lg ? '100%' : '70%',
        border: breakpoints.sm ? '1rem solid #eceeef' : null,
        backgroundColor: breakpoints.sm ? '#eceeef' : null,
        borderRadius: breakpoints.sm ? '32px' : '15px',
        boxShadow: breakpoints.sm ? 'rgba(0, 0, 0, 0.1) 0px 20px 40px' : null,
      }}
      align={Frame.ALIGNMENTS.STRETCH_CENTER}
    >
      <Frame
        gap={Frame.GAPS.NONE}
        className={`container ${
          SageClassnames.SPACERS[breakpoints.md ? 'NONE' : 'LG_TOP']
        }`}
        style={{
          width: [breakpoints.md ? '100%' : '732px'],
          boxShadow: breakpoints.sm ? null : 'rgba(0, 0, 0, 0.1) 0px 20px 40px',
          borderRadius: breakpoints.sm ? '24px' : '15px',
          height: breakpoints.md ? '576px' : '720px',
          overflowY: 'scroll',
        }}
      >
        {/* Mock window component with 3 buttons */}
        {breakpoints.sm || (
          <Frame
            background="#ECEEEF"
            style={{
              height: '42px',
              borderTopLeftRadius: '15px',
              borderTopRightRadius: '15px',
            }}
          >
            <Frame
              direction={Frame.DIRECTIONS.HORIZONTAL}
              padding={Frame.PADDINGS.SM}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                }}
              ></div>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                  marginLeft: '-15px',
                }}
              ></div>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                  marginLeft: '-15px',
                }}
              ></div>
            </Frame>
          </Frame>
        )}

        {/* Course navigation component */}
        <Frame
          background="#FFFFFF"
          style={{ height: '61px' }}
          direction={Frame.DIRECTIONS.HORIZONTAL}
          padding={Frame.PADDINGS.SM}
        >
          <Frame
            padding={Frame.PADDINGS.XS}
            direction={Frame.DIRECTIONS.HORIZONTAL}
          >
            <div
              style={{
                width: 87,
                height: breakpoints.sm ? 8 : 12,
                backgroundColor: '#D3D5D9',
                borderRadius: '100px',
              }}
            ></div>
          </Frame>
          {breakpoints.sm ? (
            <Frame
              padding={Frame.PADDINGS.NONE}
              align={Frame.ALIGNMENTS.TOP_RIGHT}
              gap={Frame.GAPS.XXS}
            >
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
            </Frame>
          ) : (
            <Frame
              padding={Frame.PADDINGS.XS}
              align={Frame.ALIGNMENTS.TOP_RIGHT}
              direction={Frame.DIRECTIONS.HORIZONTAL}
            >
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
            </Frame>
          )}
        </Frame>

        {/* Course hero image component */}
        <Frame
          style={{
            minHeight: '180px',
            backgroundImage: `
                            linear-gradient(rgba(0,0,0,.65), rgba(0,0,0,.65)),
                            url(${heroImageUrl})
                        `,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
          padding={Frame.PADDINGS.LG}
          gap={Frame.GAPS.XS}
          align={Frame.ALIGNMENTS.CENTER_CENTER}
        >
          <h5
            style={{
              fontSize: breakpoints.md ? 24 : 20,
              fontWeight: 700,
              textAlign: 'center',
            }}
            className={`${SageClassnames.TYPE.HEADING_5} ${SageClassnames.TYPE_COLORS.WHITE}`}
          >
            {courseData.title}
          </h5>
          <p
            style={{
              fontSize: 12,
              fontWeight: 500,
              textAlign: 'center',
              lineHeight: '16px',
            }}
            className={`${SageClassnames.TYPE.BODY_SMALL_MED} ${SageClassnames.TYPE_COLORS.WHITE}`}
          >
            {courseData.description}
          </p>
          {breakpoints.md ? null : (
            <div
              style={{
                width: 120,
                height: 28,
                backgroundColor: '#0072EF',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
              }}
              className={SageClassnames.SPACERS.XS_TOP}
            >
              <p style={{ fontSize: '12px', color: 'white', margin: 'auto' }}>
                Start the course
              </p>
            </div>
          )}
        </Frame>

        {/* Course content component */}
        <Frame
          style={{
            borderBottomLeftRadius: breakpoints.md ? '32px' : '15px',
            borderBottomRightRadius: breakpoints.md ? '32px' : '15px',
            paddingTop: breakpoints.md ? '24px' : '40px',
            paddingBottom: '24px',
            backgroundColor: '#ffffff',
          }}
          padding={Frame.PADDINGS[breakpoints.md ? 'SM' : 'XXL']}
          gap={Frame.GAPS.NONE}
          align={Frame.ALIGNMENTS.TOP_CENTER}
          direction={Frame.DIRECTIONS.HORIZONTAL}
        >
          <Grid>
            <Grid.Row
              horizontalAlignment={
                Grid.Row.HORIZONTAL_ALIGNMENT_TYPES.SPACE_BETWEEN
              }
            >
              {/* Left side */}
              <Grid.Col size={8}>
                {courseData.sections.length > 0 &&
                  courseData.sections.map((section: any) => (
                    <Grid key={section.title} style={{ paddingBottom: '24px' }}>
                      <Grid.Row spacerBelow="sm">
                        <Grid.Col size={12} key={section.title}>
                          <h3
                            className={
                              breakpoints.md ? '' : 'preview-lesson-title'
                            }
                            style={{
                              fontSize: breakpoints.md ? 18 : 16,
                              fontWeight: 550,
                            }}
                          >
                            {section.title}
                          </h3>
                        </Grid.Col>
                      </Grid.Row>
                      {section.lessons.length > 0 &&
                        section.lessons.map((lesson: any, i: number) => (
                          <Grid.Row key={lesson.title} spacerBelow="sm">
                            <Grid.Col
                              size={4}
                              style={{
                                maxWidth: breakpoints.md ? '40%' : null,
                              }}
                            >
                              <Frame
                                className="preview-image"
                                style={{
                                  backgroundImage: `url(${lesson?.image?.image_url})`,
                                  alignItems: 'center',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <img
                                    style={{
                                      margin: '19px auto auto',
                                      marginTop: '19px',
                                    }}
                                    src={PlayButton}
                                  ></img>
                                </div>
                              </Frame>
                            </Grid.Col>
                            <Grid.Col
                              size={8}
                              style={{
                                maxWidth: breakpoints.md ? '60%' : null,
                              }}
                            >
                              <h6
                                style={{
                                  fontSize: 14,
                                  fontWeight: 550,
                                  marginBottom: '2px',
                                }}
                              >
                                {lesson.title}
                              </h6>
                              <p
                                style={{
                                  fontSize: 12,
                                  fontWeight: 400,
                                }}
                              >
                                {lesson.description}
                              </p>
                            </Grid.Col>
                          </Grid.Row>
                        ))}
                    </Grid>
                  ))}
              </Grid.Col>

              {/* Right side */}
              <Grid.Col size={4} style={{ width: '200px' }}>
                <Card className="preview-bio" style={{ padding: '24px 0px' }}>
                  <Frame align={Frame.ALIGNMENTS.CENTER_CENTER}>
                    <IconCard
                      icon="user"
                      round
                      size="xl"
                      style={{ height: 64, width: 64, margin: '0 auto' }}
                    />
                    <h6
                      style={{
                        fontSize: 16,
                        fontWeight: 500,
                        textAlign: 'center',
                        marginTop: -10,
                        marginBottom: -20,
                      }}
                    >
                      Your name
                    </h6>
                    <p
                      style={{
                        fontSize: 14,
                        marginBottom: -5,
                        textAlign: 'center',
                      }}
                    >
                      Course Instructor
                    </p>
                    <p
                      style={{
                        fontSize: 12,
                        width: '80%',
                        textAlign: 'center',
                      }}
                    >
                      {courseData.instructor_bio}
                    </p>
                  </Frame>
                </Card>
                <Card
                  className="preview-shortcut"
                  style={{ marginTop: 50, paddingBottom: '12px' }}
                >
                  <Frame
                    align={Frame.ALIGNMENTS.CENTER_LEFT}
                    gap={Frame.GAPS.NONE}
                  >
                    <h6
                      style={{
                        fontSize: 16,
                        textAlign: 'center',
                        fontWeight: 500,
                        marginBottom: '12px',
                      }}
                    >
                      Section Shortcuts
                    </h6>
                    {courseData.sections.length > 0 &&
                      courseData.sections.map((section: any) => (
                        <div key={section.title}>
                          <p style={{ fontSize: 12, fontWeight: 500 }}>
                            <u>{section.title}</u>
                          </p>
                          <p
                            style={{
                              fontSize: 12,
                              fontWeight: 450,
                              paddingBottom: '12px',
                            }}
                          >
                            {section.lessons.length} lessons available
                          </p>
                        </div>
                      ))}
                  </Frame>
                </Card>
              </Grid.Col>
            </Grid.Row>
          </Grid>
        </Frame>
      </Frame>
    </Frame>
  );
};

export default CoursePreview;
